<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
    class="draggableBlock"
    data-block="Spacer"
    @mouseover="mouseOver"
    @mouseleave="mouseOut"
    @click.stop="() => click()"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      style="word-break: break-word; font-size: 0px"
      :style="{ backgroundColor: bgColor, backgroundImage: gradient }"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-active="isActive"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @copy="copyToClipboard"
      ></blockActions>
      <div
        :id="id"
        :style="{ height: localHeight + 'px', ...hiddenStyle }"
        style="
          font-size: 1px;
          white-space: nowrap;
          touch-action: none;
          box-sizing: border-box;
        "
      >
        <a v-if="isActive && isHovered" class="resize-handle" style="left: 50%">
          <div class="resize-svg-center">
            <svg
              width="5"
              height="10"
              viewBox="0 0 5 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="inline"
            >
              <path
                d="M4.45813 8.84766H3.41681V3.15234H4.45813C4.93351 3.15234 5.18252 2.54297 4.84296 2.19141L2.87352 0.175781C2.66978 -0.0585938 2.33022 -0.0585938 2.12648 0.175781L0.157037 2.19141C-0.182523 2.54297 0.0664878 3.15234 0.541872 3.15234H1.60583V8.84766H0.541872C0.0664878 8.84766 -0.182523 9.45703 0.157037 9.80859L2.12648 11.8242C2.33022 12.0586 2.66978 12.0586 2.87352 11.8242L4.84296 9.80859C5.18252 9.45703 4.93351 8.84766 4.45813 8.84766Z"
                fill="white"
              />
            </svg>
          </div>
          <span style="width: 80%; color: white"
            >{{ safeParse(localHeight) }} px</span
          >
        </a>
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockProperties from "@/composables/useBlockProperties";
  import blockActions from "./blockActions.vue";
  import interact from "interactjs";
  import { TopolBlock } from "@/schemas/templateSchema";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useDebounceFn } from "@vueuse/core";
  import { ref, Ref, computed, watch, onUnmounted, onMounted } from "vue";

  const mjmlStore = useMjmlStore();

  const props = defineProps<{
    block: TopolBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    showActions,
    showPaste,
    isHovered,
    blockHeight,
    blockWidth,
    id,
    bluredOrNotHidden,
    hiddenStyle,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isCurrentElementDragged,
    showDragBorder,
    isLastColumn,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  //we use localHeight because height which is stored in $store is debounced
  const localHeight: Ref<number | string> = ref(50);
  const maxHeight = ref(300);

  const height = computed({
    get() {
      return parseInt(props.block.attributes.height);
    },
    set(val: number | string) {
      if (Number(val) <= maxHeight.value + 1 && Number(val) >= 10) {
        localHeight.value = Math.round(safeParse(val));
        updateHeight(Math.round(safeParse(val)));
      }
    },
  });

  const bgColor = computed(() => {
    const bgColor = props.block.attributes["container-background-color"];
    if (bgColor !== null || bgColor !== undefined) {
      return bgColor;
    }

    return "";
  });

  const gradient = computed(() => {
    if (props.block.attributes["container-gradient"]) {
      return props.block.attributes["container-gradient"];
    }
    return "";
  });

  const updateHeight = useDebounceFn(function (val) {
    mjmlStore.updateBlockAttribute({
      prop: "height",
      value: val + "px",
    });
  }, 300);

  function safeParse(val: string | number) {
    if (typeof val === "string") {
      return parseInt(val);
    }
    return val;
  }

  watch([height, isActive], (newValue, oldValue) => {
    if (newValue[0] !== oldValue[0]) {
      localHeight.value = newValue[0];
    }

    if (newValue[1]) {
      const element = document.getElementById(id.value);
      if (element) {
        interact("#" + id.value)
          .resizable({
            edges: {
              left: false,
              right: false,
              top: false,
              bottom: ".resize-handle",
            },
          })
          .on("resizemove", (event) => {
            height.value = event.rect.height;
          });
      }
    } else {
      const element = document.getElementById(id.value);
      if (element) {
        interact("#" + id.value).unset();
      }
    }
  });

  onMounted(() => {
    if (height.value) {
      localHeight.value = height.value;
    }
  });

  onUnmounted(() => {
    const element = document.getElementById(id.value);
    if (element) {
      interact("#" + id.value).unset();
    }
  });
</script>

<style scoped>
  a.resize-handle span {
    font-family: Inter;
    font-weight: 600;
    font-size: 10px;
  }

  a.resize-handle {
    cursor: ns-resize;
    position: absolute;
    display: flex;
    justify-content: space-around;
    align-items: center;
    background-color: #3c4048;
    color: white;
    width: 68px;
    height: 20px;
    bottom: -10px;
    border-radius: 13px;
    font-size: 8px;
    text-align: center;
    line-height: 31px;
    z-index: 999;
    left: 50%;
    margin-left: -20px;
  }

  a.resize-handle:hover {
    background-color: #13171e;
  }

  .resize-svg-center {
    display: flex;
    padding-left: 10px;
    align-items: center;
  }
</style>
